import React, { useState } from "react";
import tripArrow from "../../../assets/images/trip_arrow.png";
import "../css/TripCard.css";
import { formatDateTime, formatToCustomDate, formatDateTimeForUI, getTimezoneString } from "../../../utils/dateUtils";
import {
  ClearanceStatus,
  getStatusInfo,
  CORRECTION_OPTIONS,
  EXEMPTION_REASON_OPTIONS,
  TRIP_STATUS,
  CREATOR_TYPES,
} from "../../../utils/constants";
import { airportCodeOnApprove, renderAirportCode } from "../../../utils/Utils";
import Tooltip from "@mui/material/Tooltip";
import infoIcon from "../../../assets/images/info_icon.svg";

const TripCard = ({ tripData, afterHoursStatus, onClick, isHistoric }) => {
  const clearanceDepartureAfterStatus = tripData?.requiresDepartureOvertime
    ? tripData?.overtimeDepartureStatus
    : null;

  const clearanceArrivalAfterStatus = tripData?.requiresArrivalOvertime
    ? tripData?.overtimeArrivalStatus
    : null;

  const {
    text: departureAfterHoursStatusText,
    className: departureAfterHoursClassName,
  } = getStatusInfo(clearanceDepartureAfterStatus);
  const {
    text: arrivalAfterHoursStatusText,
    className: arrivalAfterHoursClassName,
  } = getStatusInfo(clearanceArrivalAfterStatus);
  const departure = formatDateTime(tripData?.departureTime);
  const arrival = formatDateTime(tripData?.arrivalTime);

  const departureTerminal =
    tripData?.departureAirportTerminal?.name || "Main Terminal";
  const arrivalTerminal =
    tripData?.arrivalAirportTerminal?.name || "Main Terminal";

  const escortArrivalStatus =
    tripData?.escortArrivalStatus === ClearanceStatus.NOT_REQUIRED
      ? null
      : tripData?.escortArrivalStatus;
  const escortDepartureStatus =
    tripData?.escortDepartureStatus === ClearanceStatus.NOT_REQUIRED
      ? null
      : tripData?.escortDepartureStatus;
  const { text: arrivalStatusText } = getStatusInfo(escortArrivalStatus);
  const { text: departureStatusText } = getStatusInfo(escortDepartureStatus);

  const getAfterHoursCombinedClassName = () => {
    if (
      clearanceDepartureAfterStatus === ClearanceStatus.CLEARANCE_REJECTED ||
      clearanceArrivalAfterStatus === ClearanceStatus.CLEARANCE_REJECTED ||
      clearanceDepartureAfterStatus === ClearanceStatus.CANCELLED ||
      clearanceArrivalAfterStatus === ClearanceStatus.CANCELLED
    ) {
      return "rejected";
    }

    if (
      ((clearanceDepartureAfterStatus === ClearanceStatus.CLEARANCE_GRANTED ||
        clearanceDepartureAfterStatus === ClearanceStatus.PAID) &&
        !clearanceArrivalAfterStatus) ||
      (!clearanceDepartureAfterStatus &&
        (clearanceArrivalAfterStatus === ClearanceStatus.CLEARANCE_GRANTED ||
          clearanceArrivalAfterStatus === ClearanceStatus.PAID)) ||
      ((clearanceDepartureAfterStatus === ClearanceStatus.CLEARANCE_GRANTED ||
        clearanceDepartureAfterStatus === ClearanceStatus.PAID) &&
        (clearanceArrivalAfterStatus === ClearanceStatus.CLEARANCE_GRANTED ||
          clearanceArrivalAfterStatus === ClearanceStatus.PAID))
    ) {
      return "approved";
    }

    return "pending";
  };

  const getCorrectionReasonLabel = (value) => {
    const option = CORRECTION_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  const [departureTooltipOpen, setDepartureTooltipOpen] = useState(false);
  const [arrivalTooltipOpen, setArrivalTooltipOpen] = useState(false);

  const getExemptionReasonLabel = (value) =>
    EXEMPTION_REASON_OPTIONS.find((opt) => opt.value === value)?.label ?? value ?? "";

  const getTripStatusDisplay = () => {
    if (tripData?.status === TRIP_STATUS.CANCELLED) {
      return { text: "Cancelled", className: "status-cancelled" };
    }
    if (tripData?.status === TRIP_STATUS.COMPLETED && tripData?.isExempted) {
      return { text: "Exempted", className: "status-exempted" };
    }
    if (tripData?.status === TRIP_STATUS.COMPLETED) {
      return { text: "Completed", className: "status-completed" };
    }
    return null;
  };

  const tripStatusDisplay = getTripStatusDisplay();

  const renderStatusWithIcon = (
    code,
    status,
    correctionReason,
    isArrival = false
  ) => (
    <p className="status-p">
      {code} •{" "}
      <span className="status-with-icon">
        {status}
        {status === "Correction Requested" && (
          <Tooltip
            title={getCorrectionReasonLabel(correctionReason)}
            arrow
            open={isArrival ? arrivalTooltipOpen : departureTooltipOpen}
            onClose={() =>
              isArrival
                ? setArrivalTooltipOpen(false)
                : setDepartureTooltipOpen(false)
            }
            onClick={(e) => {
              e.stopPropagation();
              if (isArrival) {
                setArrivalTooltipOpen(!arrivalTooltipOpen);
                setDepartureTooltipOpen(false);
              } else {
                setDepartureTooltipOpen(!departureTooltipOpen);
                setArrivalTooltipOpen(false);
              }
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              if (isArrival) {
                setArrivalTooltipOpen(!arrivalTooltipOpen);
                setDepartureTooltipOpen(false);
              } else {
                setDepartureTooltipOpen(!departureTooltipOpen);
                setArrivalTooltipOpen(false);
              }
            }}
          >
            <img src={infoIcon} alt="info" className="status-info-icon" />
          </Tooltip>
        )}
      </span>
    </p>
  );

  return (
    <div className="trip-request-card" onClick={onClick}>
      <div className="trip-header">
        {tripStatusDisplay ? (
          <div className="trip-header-with-status">
            <div className="trip-status">
              Status
              <span className={`status-with-icon ${tripStatusDisplay.className}`}>
                {" "}
                • {tripStatusDisplay.text}
                {tripData?.status === TRIP_STATUS.COMPLETED && tripData?.isExempted && (
                  <Tooltip
                    title={getExemptionReasonLabel(tripData?.tripExemptionReason) || "Exemption reason"}
                    arrow
                    placement="bottom"
                    PopperProps={{ className: "tripcard-tooltip-popper" }}
                  >
                    <span
                      className="exemption-tooltip-trigger"
                      onClick={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                    >
                      <img src={infoIcon} alt="Exemption reason" className="status-info-icon" />
                    </span>
                  </Tooltip>
                )}
              </span>
            </div>
            {tripData?.createdAt && (
              <div className="trip-creation-date trip-creation-date-center">
                Creation Date:
                <span>{formatDateTimeForUI(tripData.createdAt)}</span>
              </div>
            )}
            <div className="trip-request-id">
              ID
              <span>{tripData.trackingId}</span>
            </div>
          </div>
        ) : (
          <div className="trip-header-info">
            {tripData?.createdAt && (
              <div className="trip-creation-date">
                Creation Date:
                <span>{formatDateTimeForUI(tripData.createdAt)}</span>
              </div>
            )}
            <div className="trip-request-id">
              ID
              <span>{tripData.trackingId}</span>
            </div>

          </div>
        )}
      </div>
      <div className="auto-generated-trip-row">
        <span className="created-by-text">
          Created by:
          <span>
            {tripData?.createdByAdsb ? CREATOR_TYPES.SYSTEM : tripData?.creatorName || '---'}
          </span>
        </span>

        <div className="trip-request-id">
          Contact No.
          <span>{tripData?.aircraft?.ownerProfile?.phone}</span>
        </div>
      </div>
      <div className="auto-generated-trip-row auto-generated-trip-row--email">
        <span className="created-by-text">
          Email:
          <span>
            {tripData?.aircraft?.ownerProfile?.email}
          </span>
        </span>
        {tripData?.createdByAdsb && (
          <span className="auto-generated-trip-text">Auto Generated Trip</span>
        )}
      </div>

      {(tripData?.departureAirport?.icaoCode ||
        tripData?.departureAirport?.iataCode ||
        tripData?.arrivalAirport?.icaoCode ||
        tripData?.arrivalAirport?.iataCode) && (
          <div className="trip-details">
            {(tripData?.departureAirport?.icaoCode ||
              tripData?.departureAirport?.iataCode) && (
                <p className="trip-request-left">
                  {renderAirportCode(
                    tripData?.departureAirport?.iataCode,
                    tripData.departureAirport.icaoCode
                  )}
                </p>
              )}
            <img src={tripArrow} className="trip-request-arrow-logo" alt="logo" />
            {(tripData?.arrivalAirport?.icaoCode ||
              tripData?.arrivalAirport?.iataCode) && (
                <p className="trip-request-right">
                  {renderAirportCode(
                    tripData?.arrivalAirport?.iataCode,
                    tripData.arrivalAirport.icaoCode
                  )}
                </p>
              )}
          </div>
        )}

      <div className="trip-request-flight-info">
        <div className="trip-request-departure-info">
          {departure?.time && (
            <p>
              <strong>Departure</strong>
              <span>{departure.time} ({getTimezoneString()})</span>
            </p>
          )}
          {departure?.date && (
            <p>
              <strong>Date</strong>
              <span>{formatToCustomDate(departure.date)}</span>
            </p>
          )}
          {departureTerminal && (
            <p>
              <strong>Terminal</strong>
              <span>{departureTerminal}</span>
            </p>
          )}
        </div>
        <div className="trip-request-arrival-info">
          {arrival?.time && (
            <p>
              <strong>Arrival</strong>
              <span>{arrival.time} ({getTimezoneString()})</span>
            </p>
          )}
          {arrival?.date && (
            <p>
              <strong>Date</strong>
              <span>{formatToCustomDate(arrival.date)}</span>
            </p>
          )}
          {arrivalTerminal && (
            <p>
              <strong>Terminal</strong>
              <span>{arrivalTerminal}</span>
            </p>
          )}
        </div>
      </div>

      {(tripData?.passengers || tripData?.aircraft) && (
        <div className="trip-request-passenger-info">
          {tripData?.passengers && (
            <p>
              <strong>Number of Passengers</strong>
              <span>{tripData.passengers.length}</span>
            </p>
          )}
          {tripData?.aircraft && (
            <p>
              <strong>Registration Number</strong>
              <span>{tripData.aircraft.displayRegistrationNumber}</span>
            </p>
          )}
          {tripData?.aircraft && (
            <p>
              <strong>Primary Account Owner</strong>
              <span>
                {`${tripData.aircraft.ownerProfile.firstName} ${tripData.aircraft.ownerProfile.lastName}`}
              </span>
            </p>
          )}
        </div>
      )}

      {(escortArrivalStatus || escortDepartureStatus || afterHoursStatus) && (
        <div className="trip-request-status-section">
          {(escortArrivalStatus || escortDepartureStatus) && (
            <div className="trip-request-status special">
              <p className="title-p">Special Onboarding Screening Status</p>
              {escortDepartureStatus != null &&
                renderStatusWithIcon(
                  airportCodeOnApprove(
                    tripData?.departureAirport?.iataCode,
                    tripData?.departureAirport?.icaoCode
                  ),
                  departureStatusText,
                  tripData?.escortDepartureCorrectionReason,
                  false
                )}
              {escortArrivalStatus != null &&
                renderStatusWithIcon(
                  airportCodeOnApprove(
                    tripData?.arrivalAirport?.iataCode,
                    tripData?.arrivalAirport?.icaoCode
                  ),
                  arrivalStatusText,
                  tripData?.escortArrivalCorrectionReason,
                  true
                )}
            </div>
          )}

          {(clearanceDepartureAfterStatus || clearanceArrivalAfterStatus) && (
            <div
              className={`trip-request-status after-hours ${getAfterHoursCombinedClassName()}`}
            >
              <p className="title-p">After Hours Status</p>
              {clearanceDepartureAfterStatus != null && (
                <p className="status-p">
                  {airportCodeOnApprove(
                    tripData?.departureAirport?.iataCode,
                    tripData?.departureAirport?.icaoCode
                  )}{" "}
                  • {departureAfterHoursStatusText}
                </p>
              )}
              {clearanceArrivalAfterStatus != null && (
                <p className="status-p">
                  {airportCodeOnApprove(
                    tripData?.arrivalAirport?.iataCode,
                    tripData?.arrivalAirport?.icaoCode
                  )}{" "}
                  • {arrivalAfterHoursStatusText}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TripCard;
